import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw, Plus, Minus, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { useStableSearch } from '@/hooks/useStableSearch';
import { formatJpWithAlias } from '@/utils/partDisplay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bangladesh Export Order Modal
 * Used when a Bangladesh customer (country_code: 880) is selected from the main order creation flow
 */
const BangladeshExportOrderModal = ({ 
  isOpen, 
  onClose, 
  onCreated, 
  token,
  preSelectedCustomer = null // Customer already selected from parent component
}) => {
  const [step, setStep] = useState(preSelectedCustomer ? 2 : 1);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(preSelectedCustomer);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [items, setItems] = useState([]);
  const [pfCharge, setPfCharge] = useState(0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Stable part search with debounce + abort + 2.5s verification
  const partSearchFn = useCallback(async (query, signal) => {
    const response = await fetch(
      `${API_URL}/api/categories/All%20Products/parts?search=${encodeURIComponent(query)}&limit=20&include_inactive_brands=1&include_admin_only=true`,
      { headers: { 'Authorization': `Bearer ${token}` }, signal }
    );
    const data = await response.json();
    const parts = data?.parts || data || [];
    return Array.isArray(parts) ? parts : [];
  }, [token]);

  const { results: searchResults, isSearching: searching, setQuery: doPartSearch } = useStableSearch(partSearchFn, 500);

  useEffect(() => { doPartSearch(partSearch); }, [partSearch, doPartSearch]);

  // Update selectedCustomer when preSelectedCustomer changes
  useEffect(() => {
    if (preSelectedCustomer) {
      setSelectedCustomer(preSelectedCustomer);
      setStep(2);
    }
  }, [preSelectedCustomer]);

  // Fetch customers and agents
  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      fetchAgents();
    }
  }, [isOpen]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/export-orders/export-customers?country=bangladesh&search=${customerSearch}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/export-orders/export-agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // fetchCustomers and fetchAgents remain manual (not fast-typing)

  const addItem = (product) => {
    const existingIndex = items.findIndex(i => i.jp_part_no === product.jp_part_no);
    if (existingIndex >= 0) {
      const newItems = [...items];
      newItems[existingIndex].quantity += 1;
      newItems[existingIndex].subtotal = newItems[existingIndex].quantity * newItems[existingIndex].shake_hand_price;
      setItems(newItems);
    } else {
      setItems([...items, {
        jp_part_no: product.jp_part_no,
        part_name: product.part_name,
        oe_part_no: product.oe_part_no || '',
        mrp: product.mrp,
        shake_hand_price: product.mrp,
        quantity: 1,
        subtotal: product.mrp
      }]);
    }
    setPartSearch('');
  };

  const updateItemPrice = (index, price) => {
    const newItems = [...items];
    newItems[index].shake_hand_price = parseFloat(price) || 0;
    newItems[index].subtotal = newItems[index].quantity * newItems[index].shake_hand_price;
    setItems(newItems);
  };

  const updateItemQty = (index, qty) => {
    const newItems = [...items];
    newItems[index].quantity = parseInt(qty) || 1;
    newItems[index].subtotal = newItems[index].quantity * newItems[index].shake_hand_price;
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.subtotal, 0) + parseFloat(pfCharge || 0);
  };

  const handleSubmit = async () => {
    if (submitting || loading) return;
    
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSubmitting(true);
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/export-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: selectedCustomer.customer_id,
          country: 'bangladesh',
          items: items,
          packing_forwarding_charge: parseFloat(pfCharge) || 0,
          export_agent_id: selectedAgent && selectedAgent !== 'none' ? selectedAgent : null,
          notes: notes,
          private_marka: selectedCustomer.private_marka
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Bangladesh export order ${data.order_id} created successfully`);
        onCreated?.(data);
        handleClose();
      } else {
        toast.error(data.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Error creating order');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(preSelectedCustomer ? 2 : 1);
    setSelectedCustomer(preSelectedCustomer);
    setItems([]);
    setPfCharge(0);
    setNotes('');
    setPartSearch('');
    doPartSearch('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xl">🇧🇩</span>
            Create Bangladesh Export Order {preSelectedCustomer ? '' : `- Step ${step}/2`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && !preSelectedCustomer && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium">Export Agent (Optional)</label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger><SelectValue placeholder="Select agent..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Agent</SelectItem>
                    {agents.map(agent => (
                      <SelectItem key={agent.customer_id} value={agent.customer_id}>
                        {agent.company_name || agent.name} ({agent.agent_markup_percentage}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Search Bangladesh Customer</label>
              <Input
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  fetchCustomers();
                }}
                placeholder="Search by name, company, or mobile..."
              />
            </div>

            <div className="border rounded-md max-h-64 overflow-y-auto">
              {customers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No Bangladesh customers found.
                </div>
              ) : (
                customers.map(cust => (
                  <div
                    key={cust.customer_id}
                    className={`p-3 border-b cursor-pointer hover:bg-green-50 ${
                      selectedCustomer?.customer_id === cust.customer_id ? 'bg-green-100 border-green-300' : ''
                    }`}
                    onClick={() => setSelectedCustomer(cust)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{cust.company_name || cust.name}</div>
                        <div className="text-sm text-gray-500">
                          +{cust.country_code || '880'} {cust.mobile}
                        </div>
                      </div>
                      {cust.private_marka && (
                        <Badge variant="outline" className="bg-green-50">{cust.private_marka}</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!selectedCustomer}
                className="bg-green-600 hover:bg-green-700"
              >
                Next: Add Items
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-md border border-green-200">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-green-800">{selectedCustomer?.company_name || selectedCustomer?.name}</div>
                  <div className="text-sm text-green-600">
                    +{selectedCustomer?.country_code || '880'} {selectedCustomer?.mobile} | 
                    Private Marka: {selectedCustomer?.private_marka || 'N/A'}
                  </div>
                </div>
                <Badge className="bg-green-600">Bangladesh Export</Badge>
              </div>
            </div>

            {/* Agent Selection */}
            <div>
              <label className="text-sm font-medium">Export Agent (Optional)</label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger><SelectValue placeholder="Select agent..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Agent</SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.customer_id} value={agent.customer_id}>
                      {agent.company_name || agent.name} ({agent.agent_markup_percentage}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Part Search */}
            <div>
              <label className="text-sm font-medium">Search Parts</label>
              <div className="relative">
                <Input
                  value={partSearch}
                  onChange={(e) => setPartSearch(e.target.value)}
                  placeholder="Search by part number or name..."
                />
                {searching && (
                  <RefreshCw className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-md max-h-48 overflow-y-auto bg-white shadow-lg">
                  {searchResults.map(product => (
                    <div
                      key={product.jp_part_no}
                      className="p-2 border-b hover:bg-green-50 cursor-pointer flex justify-between items-center"
                      onClick={() => addItem(product)}
                    >
                      <div>
                        <div className="font-medium text-sm">{formatJpWithAlias(product)}</div>
                        <div className="text-xs text-gray-500">{product.part_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-green-600">₹{product.mrp}</div>
                        <Plus className="h-4 w-4 text-green-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Items List */}
            <div className="border rounded-md">
              <div className="bg-gray-100 p-2 font-medium text-sm grid grid-cols-12 gap-2">
                <div className="col-span-3">Part No</div>
                <div className="col-span-3">Part Name</div>
                <div className="col-span-2 text-center">Price</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-1 text-right">Subtotal</div>
                <div className="col-span-1"></div>
              </div>
              
              <div className="max-h-48 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    No items added yet. Search and add parts above.
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <div key={idx} className="p-2 border-b grid grid-cols-12 gap-2 items-center text-sm">
                      <div className="col-span-3 font-medium">{formatJpWithAlias(item)}</div>
                      <div className="col-span-3 text-xs text-gray-600 truncate">{item.part_name}</div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.shake_hand_price}
                          onChange={(e) => updateItemPrice(idx, e.target.value)}
                          className="h-8 text-center"
                        />
                      </div>
                      <div className="col-span-2 flex items-center justify-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateItemQty(idx, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItemQty(idx, e.target.value)}
                          className="h-8 w-14 text-center"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateItemQty(idx, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="col-span-1 text-right font-medium">₹{item.subtotal}</div>
                      <div className="col-span-1 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => removeItem(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* P&F Charge and Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">P&F Charge</label>
                <Input
                  type="number"
                  value={pfCharge}
                  onChange={(e) => setPfCharge(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Order notes..."
                  className="h-10"
                />
              </div>
            </div>

            {/* Total */}
            <div className="bg-green-100 p-3 rounded-md flex justify-between items-center">
              <span className="font-medium">Total Amount:</span>
              <span className="text-xl font-bold text-green-700">₹{calculateTotal().toLocaleString('en-IN')}</span>
            </div>

            <DialogFooter>
              {!preSelectedCustomer && (
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              )}
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={items.length === 0 || loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Bangladesh Order
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BangladeshExportOrderModal;
